import {
  ArrowCounterClockwiseIcon,
  ArrowLeftIcon,
  CheckIcon,
  CopyIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  SignInIcon,
  UserPlusIcon,
  XIcon,
} from '@phosphor-icons/react'
import { type FormEvent, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  MIN_ACCOUNT_PASSWORD_LENGTH,
  accountEciesToWire,
  completeSocialLogin,
  fetchMe,
  loginEmail,
  registerEmail,
  startSocialLogin,
  verifyTonConnect,
} from '../../api/userland'
import { useAccountVault } from '../../hooks/useAccountVault'
import {
  createAccountKeyringV2,
  encryptSeedToAccountVault,
  generateLoginSecret,
  loginSecretToB64,
} from '../../lib/clientVault'
import {
  OAUTH_POPUP_NAME,
  assignLocation,
  oauthAuthorizeURL,
  subscribeOAuthSignal,
  writeOAuthResume,
} from '../../lib/oauthBridge'
import { linkedLoginMethods } from '../../lib/profileIdentity'
import { generateRecoveryKey } from '../../lib/recoveryKey'
import { clearReferredBy, readReferredBy } from '../../lib/referredBy'
import { clearSocialPending, readSocialPending, writeSocialPending } from '../../lib/socialPending'
import { isTelegramWebView, socialResumeUrl } from '../../lib/telegramWebView'
import {
  type TonConnectProofBundle,
  isTonConnectCancelled,
  prefetchTonConnect,
  prefetchTonConnectNonce,
  requestTonProof,
  takeTonConnectNonce,
} from '../../lib/tonConnectAuth'
import soft from '../../styles/soft.module.css'
import { useAccountSession } from '../../wallet/AccountSessionContext'
import { type AuthShellMode, useAuthShell } from '../../wallet/AuthShellContext'
import { useWallets } from '../../wallet/WalletsContext'
import { completeIdentityConfirmation } from '../../wallet/tradeAuth'
import { SocialGlyph } from '../common/SocialLinks'
import { TonMark } from '../exchange/icons'
import { PixelValue } from '../mobile/PixelValue'
import { SoftSpinner, usePixelToast } from '../ui/pixel-toast'
import { ExternalBrowserCard } from './AuthContinue'
import { AuthHero } from './AuthHero'
import styles from './AuthShell.module.css'

type Pane =
  | 'chooser'
  | 'login'
  | 'signup'
  | 'confirm'
  | 'ton-recovery'
  | 'ton-unlock'
  | 'external-browser'

const WRONG_ACCOUNT = 'This is not the account you’re signed into'

export function AuthShell() {
  const { open, mode, closeAuth, openAuth } = useAuthShell()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [hideForWallet, setHideForWallet] = useState(false)
  const hideForWalletRef = useRef(false)

  useEffect(() => {
    // Best-effort warm-up. Both helpers re-throw on purpose so a caller that
    // actually NEEDS the value sees the failure — but `void` does not catch,
    // so a failed prefetch here became an unhandled rejection (offline, a 500,
    // or simply no session yet). Nothing on this path depends on it.
    void prefetchTonConnect().catch(() => undefined)
    void prefetchTonConnectNonce().catch(() => undefined)
  }, [])

  useLayoutEffect(() => {
    const el = dialogRef.current
    if (!el) return
    const show = open && !hideForWallet
    if (show && !el.open) el.showModal()
    if (!show && el.open) el.close()
  }, [hideForWallet, open])

  useEffect(() => {
    if (open) return
    hideForWalletRef.current = false
    setHideForWallet(false)
  }, [open])

  const hideForWalletNow = (hide: boolean) => {
    hideForWalletRef.current = hide
    // Native <dialog showModal> is a top layer. TON Connect's overlay cannot
    // paint above it, so the dialog must close in this turn — not after paint.
    const el = dialogRef.current
    if (el) {
      if (hide && el.open) el.close()
      if (!hide && !el.open) el.showModal()
    }
    setHideForWallet(hide)
  }

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-label="Log in"
      onClose={() => {
        if (hideForWalletRef.current) return
        completeIdentityConfirmation(false)
        closeAuth()
      }}
      onCancel={(event) => {
        if (hideForWalletRef.current) event.preventDefault()
      }}
    >
      {open ? (
        <AuthShellBody
          mode={mode}
          onClose={closeAuth}
          onMode={openAuth}
          onHideForWallet={hideForWalletNow}
        />
      ) : null}
    </dialog>
  )
}

function AuthShellBody({
  mode,
  onClose,
  onMode,
  onHideForWallet,
}: {
  mode: AuthShellMode
  onClose: () => void
  onMode: (mode: AuthShellMode) => void
  onHideForWallet: (hide: boolean) => void
}) {
  const session = useAccountSession()
  const { reload } = useWallets()
  const vault = useAccountVault()
  const pixelToast = usePixelToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [recoveryKey, setRecoveryKey] = useState(() => generateRecoveryKey())
  const [saved, setSaved] = useState(false)
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorTick, setErrorTick] = useState(0)
  const [askLeave, setAskLeave] = useState(false)
  const [revealKey, setRevealKey] = useState(false)
  const [pane, setPane] = useState<Pane>(paneFromMode(mode))
  const [unlockKey, setUnlockKey] = useState('')
  const [emailOpen, setEmailOpen] = useState(false)
  const [resumeUrl, setResumeUrl] = useState('')
  const [pendingMethod, setPendingMethod] = useState<'telegram' | 'google' | 'ton' | null>(null)
  const pendingProof = useRef<TonConnectProofBundle | null>(null)
  const connectingRef = useRef(false)
  const paneRef = useRef(pane)
  paneRef.current = pane
  const methods = session.me ? linkedLoginMethods(session.me) : []

  useEffect(() => {
    setPane(paneFromMode(mode))
    setError(null)
    setStep(1)
    setAskLeave(false)
    setEmailOpen(false)
  }, [mode])

  useEffect(() => {
    if (pane !== 'confirm' || !session.me) return
    const listed = linkedLoginMethods(session.me)
    const emailMethod = listed.find((method) => method.kind === 'email')
    if (emailMethod?.label.includes('@')) setEmail(emailMethod.label)
    if (listed.length === 1 && emailMethod) setEmailOpen(true)
  }, [pane, session.me])

  useEffect(() => {
    if (pane !== 'ton-recovery' && !(pane === 'signup' && step === 3)) return
    void import('../../lib/tonMnemonic')
  }, [pane, step])

  useEffect(() => {
    const resetBusy = () => {
      setBusy(false)
      setPendingMethod(null)
    }
    window.addEventListener('pageshow', resetBusy)
    return () => window.removeEventListener('pageshow', resetBusy)
  }, [])

  const signup = pane === 'signup'
  const showError = (message: string) => {
    setError(message)
    setErrorTick((tick) => tick + 1)
  }

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const submitted = password
    setPassword('')
    try {
      await loginEmail(email, submitted)
      await vault.confirmAccountPassword(submitted)
      await session.reload()
      await reload()
      completeIdentityConfirmation(true)
      onClose()
    } catch (err) {
      showError(pane === 'confirm' ? confirmFailMessage(err) : 'Email or password is incorrect')
    } finally {
      setBusy(false)
    }
  }

  const submitSignup = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    if (step === 1) {
      if (!email.includes('@')) {
        if (email.length === 0) showError('A valid email address is required')
        return
      }
      setStep(2)
      return
    }
    if (step === 2) {
      if (password.length < MIN_ACCOUNT_PASSWORD_LENGTH) {
        if (password.length === 0) {
          showError(`Password must be at least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters`)
        }
        return
      }
      if (password !== confirm) {
        if (confirm.length === 0) showError('Passwords do not match')
        return
      }
      setStep(3)
      return
    }
    if (!saved) {
      showError('Save the recovery key before continuing')
      return
    }
    if (!walletCryptoAvailable()) {
      showError(INSECURE_CONTEXT)
      return
    }
    setBusy(true)
    const submitted = password
    try {
      const { keyring, derived, seed } = await prepareFirstWalletBundle({
        password: submitted,
        recoveryKey,
      })
      try {
        await registerEmail({
          email,
          password: submitted,
          vault: {
            version: keyring.version,
            kdf_name: keyring.kdfName,
            key_slots: keyring.keySlots,
            public_key_b64: keyring.publicKeyB64,
          },
          wallet: {
            label: 'Wallet 1',
            address: derived.address.friendly,
            state_init_boc_base64: derived.stateInitBocBase64,
            vault: accountEciesToWire(seed),
          },
          referral_code: storedReferralCode(),
        })
      } catch (err) {
        // 201+cookie can still throw (body parse, follow-up /me). If the
        // session is live the account exists — close, don't show a false fail.
        try {
          await fetchMe()
        } catch {
          showError(signupFailMessage(err))
          return
        }
      }
      // The account exists: its referral (if any) was bound at creation.
      clearReferredBy()
      setPassword('')
      setConfirm('')
      try {
        await vault.confirmAccountPassword(submitted)
      } catch {
        // Account is live. A failed unwrap still leaves a PKCS8 backup from
        // keyring mint; the next login restores signing.
      }
      await session.reload()
      await reload()
      onClose()
    } catch (err) {
      showError(signupFailMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const startTonConnect = async () => {
    if (connectingRef.current) return
    connectingRef.current = true
    setError(null)
    setPendingMethod('ton')
    try {
      const nonce = takeTonConnectNonce()
      const [{ payload }] = await Promise.all([nonce, prefetchTonConnect()])
      onHideForWallet(true)
      const proof = await requestTonProof(payload)
      pendingProof.current = proof
      try {
        await verifyTonConnect(proof)
      } catch (err) {
        if (err instanceof ApiError && err.code === 'register_required') {
          if (pane === 'confirm') throw err
          setRecoveryKey(generateRecoveryKey())
          setSaved(false)
          setRevealKey(false)
          setPane('ton-recovery')
          onHideForWallet(false)
          return
        }
        throw err
      }
      await session.reload()
      await vault.unlockFromSession()
      await reload()
      pendingProof.current = null
      completeIdentityConfirmation(true)
      onClose()
    } catch (err) {
      pendingProof.current = null
      if (isTonConnectCancelled(err)) {
        onHideForWallet(false)
        return
      }
      if (pane === 'confirm' || isWrongAccount(err)) {
        onHideForWallet(false)
        showError(confirmFailMessage(err))
        return
      }
      onClose()
      pixelToast.push({ tone: 'fail', content: tonConnectFailMessage(err), duration: 5000 })
    } finally {
      connectingRef.current = false
      setPendingMethod(null)
      void prefetchTonConnectNonce()
    }
  }

  const startSocial = async (provider: 'google' | 'telegram') => {
    if (!walletCryptoAvailable()) {
      showError(INSECURE_CONTEXT)
      return
    }
    if (isTelegramWebView()) {
      setResumeUrl(socialResumeUrl(window.location.origin, provider))
      setPane('external-browser')
      return
    }
    setPendingMethod(provider)
    setError(null)
    const intent = paneRef.current === 'confirm' ? 'confirm' : 'login'
    writeOAuthResume(intent, `${window.location.pathname}${window.location.search}`)
    // Same window as login: open on this click so the blocker cannot swallow
    // it after /start, then point it at Telegram/Google. Confirm must not
    // navigate this tab — that drops the seed waiter.
    const popup = window.open('about:blank', OAUTH_POPUP_NAME, 'popup=yes,width=480,height=720')
    try {
      const { auth_url } = await startSocialLogin(provider)
      const href = oauthAuthorizeURL(auth_url, provider, intent)
      if (!popup || popup.closed) {
        assignLocation(href)
        return
      }
      popup.location.replace(href)
      const stop = subscribeOAuthSignal((msg) => {
        if (msg.provider !== provider) return
        stop()
        void (async () => {
          if (msg.register && msg.ticket) {
            writeSocialPending({ provider, ticket: msg.ticket })
            setPendingMethod(null)
            setPane('ton-recovery')
            return
          }
          if (!msg.ok) {
            showError(msg.error || 'Login was cancelled.')
            setPendingMethod(null)
            return
          }
          await session.reload()
          await vault.unlockFromSession()
          await reload()
          if (paneRef.current === 'confirm') completeIdentityConfirmation(true)
          onClose()
          setPendingMethod(null)
        })()
      })
      const poll = window.setInterval(() => {
        if (!popup.closed) return
        window.clearInterval(poll)
        window.setTimeout(() => {
          setPendingMethod((current) => (current === provider ? null : current))
        }, 400)
      }, 400)
    } catch (err) {
      popup?.close()
      showError(
        err instanceof ApiError ? err.message : `${provider} isn’t enabled on this preview.`
      )
      setPendingMethod(null)
    }
  }

  const submitTonRecovery = async (event: FormEvent) => {
    event.preventDefault()
    if (!saved) {
      showError('Save the recovery key before continuing')
      return
    }
    const social = readSocialPending()
    const proof = pendingProof.current
    if (!social && !proof) {
      showError('Wallet proof expired. Connect again.')
      setPane('chooser')
      return
    }
    if (!walletCryptoAvailable()) {
      showError(INSECURE_CONTEXT)
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (social) {
        const loginSecret = generateLoginSecret()
        const { keyring, derived, seed } = await prepareFirstWalletBundle({
          recoveryKey,
          loginSecret,
        })
        await completeSocialLogin({
          provider: social.provider,
          ticket: social.ticket,
          vault: {
            version: keyring.version,
            kdf_name: keyring.kdfName,
            key_slots: keyring.keySlots,
            public_key_b64: keyring.publicKeyB64,
            login_secret_b64: loginSecretToB64(loginSecret),
          },
          wallet: {
            label: 'Wallet 1',
            address: derived.address.friendly,
            state_init_boc_base64: derived.stateInitBocBase64,
            vault: accountEciesToWire(seed),
          },
          referral_code: storedReferralCode(),
        })
        clearSocialPending()
        clearReferredBy()
        await vault.unlock({ loginSecret })
      } else if (proof) {
        const loginSecret = generateLoginSecret()
        const { keyring, derived, seed } = await prepareFirstWalletBundle({
          recoveryKey,
          loginSecret,
        })
        await verifyTonConnect({
          ...proof,
          vault: {
            version: keyring.version,
            kdf_name: keyring.kdfName,
            key_slots: keyring.keySlots,
            public_key_b64: keyring.publicKeyB64,
            login_secret_b64: loginSecretToB64(loginSecret),
          },
          wallet: {
            label: 'Wallet 1',
            address: derived.address.friendly,
            state_init_boc_base64: derived.stateInitBocBase64,
            vault: accountEciesToWire(seed),
          },
          referral_code: storedReferralCode(),
        })
        pendingProof.current = null
        clearReferredBy()
        await vault.unlock({ loginSecret })
      }
      await session.reload()
      await reload()
      completeIdentityConfirmation(true)
      onClose()
    } catch (err) {
      showError(social ? socialFailMessage(err) : tonConnectFailMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const submitTonUnlock = async (event: FormEvent) => {
    event.preventDefault()
    if (!unlockKey.trim()) {
      showError('Recovery key is required')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await vault.unlock({ recoveryKey: unlockKey.trim() })
      await reload()
      setUnlockKey('')
      pendingProof.current = null
      completeIdentityConfirmation(true)
      onClose()
    } catch {
      showError('Recovery key is incorrect')
    } finally {
      setBusy(false)
    }
  }

  const goChooser = () => {
    setError(null)
    setStep(1)
    setUnlockKey('')
    pendingProof.current = null
    setResumeUrl('')
    setPane('chooser')
    onMode('chooser')
  }

  const goBack = () => {
    setError(null)
    if (signup && step > 1) {
      setStep(step === 3 ? 2 : 1)
      return
    }
    goChooser()
  }

  const goLogin = () => {
    setError(null)
    setStep(1)
    setPane('login')
    onMode('login')
  }

  const goSignup = () => {
    setError(null)
    setStep(1)
    setRecoveryKey(generateRecoveryKey())
    setSaved(false)
    setPane('signup')
    onMode('signup')
  }

  const paneTitle =
    pane === 'ton-recovery' || (signup && step === 3)
      ? 'Recovery key'
      : pane === 'ton-unlock'
        ? 'Restore account'
        : signup
          ? 'Create account'
          : 'Log in'
  // the mark by a form pane's title, as a Soft dialog's head wears one
  const PaneMark =
    paneTitle === 'Recovery key'
      ? KeyIcon
      : paneTitle === 'Restore account'
        ? ArrowCounterClockwiseIcon
        : paneTitle === 'Create account'
          ? UserPlusIcon
          : SignInIcon
  const dirty =
    pane === 'confirm' ||
    pane === 'ton-recovery' ||
    (pane !== 'chooser' &&
      pane !== 'ton-unlock' &&
      (email.trim().length > 0 ||
        password.length > 0 ||
        confirm.length > 0 ||
        (signup && step > 1)))
  const requestClose = () => {
    if (dirty) setAskLeave(true)
    else onClose()
  }

  return (
    <div className={styles.sheet}>
      <button
        type="button"
        className={`${soft.btn} ${soft.round} ${soft.small} ${styles.close}`}
        aria-label="Close"
        onClick={requestClose}
      >
        <XIcon size={16} weight="bold" aria-hidden="true" />
      </button>

      {pane === 'confirm' ? (
        <div className={styles.chooser}>
          <AuthHero kind="confirm" />
          <h2 className={styles.welcome}>Confirm it’s you</h2>
          <p className={styles.lead}>Use the same login this account was created with.</p>
          {!session.me ? (
            <span className={styles.callbackSpin}>
              <SoftSpinner size={20} />
            </span>
          ) : null}
          {methods.some((method) => method.kind === 'telegram') ? (
            <MethodButton
              pending={pendingMethod === 'telegram'}
              disabled={busy || pendingMethod !== null}
              icon={
                <span className={styles.telegram}>
                  <TelegramMark />
                </span>
              }
              onClick={() => void startSocial('telegram')}
            >
              Continue with Telegram
            </MethodButton>
          ) : null}
          {methods.some((method) => method.kind === 'google') ? (
            <MethodButton
              pending={pendingMethod === 'google'}
              disabled={busy || pendingMethod !== null}
              icon={<GoogleMark />}
              onClick={() => void startSocial('google')}
            >
              Continue with Google
            </MethodButton>
          ) : null}
          {methods.some((method) => method.kind === 'ton') ? (
            <MethodButton
              pending={pendingMethod === 'ton'}
              disabled={busy || pendingMethod !== null}
              icon={<TonMark size={22} />}
              onClick={() => void startTonConnect()}
            >
              Continue with TON Connect
            </MethodButton>
          ) : null}
          {methods.some((method) => method.kind === 'email') ? (
            emailOpen ? (
              <form className={styles.form} onSubmit={submitLogin}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor="confirm-email">
                    Email
                  </label>
                  <input
                    id="confirm-email"
                    className={styles.input}
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor="confirm-password">
                    Password
                  </label>
                  <input
                    id="confirm-password"
                    className={styles.input}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className={`${soft.action} ${styles.primary}`}
                  disabled={busy}
                >
                  {busy ? 'Checking…' : 'Confirm'}
                </button>
              </form>
            ) : (
              <button
                type="button"
                className={`${soft.btn} ${styles.method}`}
                onClick={() => setEmailOpen(true)}
              >
                <EnvelopeSimpleIcon size={22} weight="bold" aria-hidden="true" />
                Continue with email
              </button>
            )
          ) : null}
          {error && (
            <p key={errorTick} className={styles.error} data-pulse="">
              {error}
            </p>
          )}
        </div>
      ) : pane === 'chooser' ? (
        <div className={styles.chooser}>
          <AuthHero kind="ways" />
          <h2 className={styles.welcome}>Welcome to Wils.trade</h2>
          <p className={styles.lead}>Continue with Telegram, Google, TON Connect or email.</p>
          <MethodButton
            pending={pendingMethod === 'telegram'}
            disabled={busy || pendingMethod !== null}
            icon={
              <span className={styles.telegram}>
                <TelegramMark />
              </span>
            }
            onClick={() => void startSocial('telegram')}
          >
            Continue with Telegram
          </MethodButton>
          <MethodButton
            pending={pendingMethod === 'google'}
            disabled={busy || pendingMethod !== null}
            icon={<GoogleMark />}
            onClick={() => void startSocial('google')}
          >
            Continue with Google
          </MethodButton>
          <MethodButton
            pending={pendingMethod === 'ton'}
            disabled={busy || pendingMethod !== null}
            icon={<TonMark size={22} />}
            onClick={() => void startTonConnect()}
          >
            Continue with TON Connect
          </MethodButton>
          <div className={styles.or}>
            <span>or</span>
          </div>
          <button
            type="button"
            className={`${soft.btn} ${styles.method}`}
            onClick={goLogin}
            disabled={busy || pendingMethod !== null}
          >
            <EnvelopeSimpleIcon size={22} weight="bold" aria-hidden="true" />
            Continue with email
          </button>
          {error && (
            <p key={errorTick} className={styles.error} data-pulse="">
              {error}
            </p>
          )}
        </div>
      ) : pane === 'external-browser' ? (
        <ExternalBrowserCard
          resumeUrl={resumeUrl || socialResumeUrl(window.location.origin, 'telegram')}
          hasTicket={Boolean(readSocialPending()?.ticket)}
          onBack={goChooser}
        />
      ) : pane === 'ton-recovery' || pane === 'ton-unlock' ? (
        <div className={styles.pane}>
          <div className={styles.head}>
            <span className={styles.headMark} aria-hidden="true">
              <PaneMark size={18} weight="bold" />
            </span>
            <h2 className={styles.headTitle}>{paneTitle}</h2>
          </div>
          <form
            className={styles.form}
            noValidate
            onSubmit={pane === 'ton-recovery' ? submitTonRecovery : submitTonUnlock}
          >
            {pane === 'ton-recovery' ? (
              <>
                <p className={styles.hint}>If you lose this key, Wils cannot recover the wallet.</p>
                <div className={styles.recoveryCard}>
                  <div className={styles.recoveryRow}>
                    <PixelValue masked={!revealKey} stamp={recoveryKey} grain="md">
                      <p className={styles.recoveryValue} data-pix>
                        {recoveryKey}
                      </p>
                    </PixelValue>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label={revealKey ? 'Hide recovery key' : 'Show recovery key'}
                      onClick={() => setRevealKey((open) => !open)}
                    >
                      {revealKey ? (
                        <EyeIcon size={16} weight="bold" aria-hidden="true" />
                      ) : (
                        <EyeSlashIcon size={16} weight="bold" aria-hidden="true" />
                      )}
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Copy recovery key"
                      onClick={() => {
                        void copyText(recoveryKey)
                      }}
                    >
                      <CopyIcon size={16} weight="bold" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <label
                  className={styles.check}
                  data-error={error === 'Save the recovery key before continuing' || undefined}
                >
                  <input
                    type="checkbox"
                    className={styles.checkNative}
                    checked={saved}
                    onChange={(event) => {
                      setSaved(event.target.checked)
                      if (event.target.checked) setError(null)
                    }}
                  />
                  <span className={styles.checkBox} aria-hidden="true">
                    {saved ? <CheckIcon size={12} weight="bold" /> : null}
                  </span>
                  I saved the recovery key
                </label>
              </>
            ) : (
              <div className={styles.field}>
                <p className={styles.hint}>
                  Restore this account on this browser. The recovery key is only for restoring the
                  account.
                </p>
                <label className={styles.fieldLabel} htmlFor="auth-ton-unlock">
                  Recovery key
                </label>
                <input
                  id="auth-ton-unlock"
                  className={styles.input}
                  type="text"
                  autoComplete="off"
                  placeholder="Recovery key"
                  aria-label="Recovery key"
                  value={unlockKey}
                  onChange={(event) => setUnlockKey(event.target.value)}
                />
              </div>
            )}
            {error && (
              <p key={errorTick} className={styles.error} data-pulse="">
                {error}
              </p>
            )}
            <button type="submit" className={`${soft.action} ${styles.primary}`} disabled={busy}>
              {busy ? 'Working…' : pane === 'ton-recovery' ? 'Create account' : 'Restore'}
            </button>
          </form>
          {pane === 'ton-recovery' ? (
            <div className={styles.footer}>
              <button type="button" className={styles.back} onClick={goChooser}>
                <ArrowLeftIcon size={14} weight="bold" aria-hidden="true" />
                Back
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.pane}>
          <div className={styles.head}>
            <span className={styles.headMark} aria-hidden="true">
              <PaneMark size={18} weight="bold" />
            </span>
            <h2 className={styles.headTitle}>{paneTitle}</h2>
          </div>
          <form className={styles.form} noValidate onSubmit={signup ? submitSignup : submitLogin}>
            {signup && step === 3 && (
              <p className={styles.hint}>
                If you lose this key and your password, Wils cannot recover the wallet.
              </p>
            )}
            {(!signup || step === 1) && (
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="auth-email">
                  Email
                </label>
                <input
                  id="auth-email"
                  className={styles.input}
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  aria-label="Email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                {signup && email.length > 0 && !email.includes('@') && (
                  <p className={styles.hintBad}>Enter a valid email</p>
                )}
              </div>
            )}
            {(!signup || step === 2) && (
              <>
                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor="auth-password">
                    Password
                  </label>
                  <input
                    id="auth-password"
                    className={styles.input}
                    type="password"
                    autoComplete={signup ? 'new-password' : 'current-password'}
                    placeholder="Password"
                    aria-label="Password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  {signup && password.length > 0 && (
                    <p
                      className={
                        password.length >= MIN_ACCOUNT_PASSWORD_LENGTH
                          ? styles.hintOk
                          : styles.hintBad
                      }
                    >
                      {password.length >= MIN_ACCOUNT_PASSWORD_LENGTH
                        ? 'Password looks good'
                        : `At least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters`}
                    </p>
                  )}
                </div>
                {signup && (
                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="auth-confirm">
                      Confirm password
                    </label>
                    <input
                      id="auth-confirm"
                      className={styles.input}
                      type="password"
                      autoComplete="new-password"
                      placeholder="Confirm password"
                      aria-label="Confirm password"
                      value={confirm}
                      onChange={(event) => setConfirm(event.target.value)}
                    />
                    {confirm.length > 0 && (
                      <p className={password === confirm ? styles.hintOk : styles.hintBad}>
                        {password === confirm ? 'Passwords match' : 'Passwords do not match'}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
            {signup && step === 3 && (
              <>
                <div className={styles.recoveryCard}>
                  <div className={styles.recoveryRow}>
                    <PixelValue masked={!revealKey} stamp={recoveryKey} grain="md">
                      <p className={styles.recoveryValue} data-pix>
                        {recoveryKey}
                      </p>
                    </PixelValue>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label={revealKey ? 'Hide recovery key' : 'Show recovery key'}
                      onClick={() => setRevealKey((open) => !open)}
                    >
                      {revealKey ? (
                        <EyeIcon size={16} weight="bold" aria-hidden="true" />
                      ) : (
                        <EyeSlashIcon size={16} weight="bold" aria-hidden="true" />
                      )}
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Copy recovery key"
                      onClick={() => {
                        void copyText(recoveryKey)
                      }}
                    >
                      <CopyIcon size={16} weight="bold" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <label
                  className={styles.check}
                  data-error={error === 'Save the recovery key before continuing' || undefined}
                >
                  <input
                    type="checkbox"
                    className={styles.checkNative}
                    checked={saved}
                    onChange={(event) => {
                      setSaved(event.target.checked)
                      if (event.target.checked) setError(null)
                    }}
                  />
                  <span className={styles.checkBox} aria-hidden="true">
                    {saved ? <CheckIcon size={12} weight="bold" /> : null}
                  </span>
                  I saved the recovery key
                </label>
              </>
            )}
            {error && (
              <p key={errorTick} className={styles.error} data-pulse="">
                {error}
              </p>
            )}
            <button type="submit" className={`${soft.action} ${styles.primary}`} disabled={busy}>
              {busy ? 'Working…' : signup ? (step === 3 ? 'Create account' : 'Continue') : 'Log in'}
            </button>
          </form>
          <div className={styles.footer}>
            <button type="button" className={styles.back} onClick={goBack}>
              <ArrowLeftIcon size={14} weight="bold" aria-hidden="true" />
              Back
            </button>
            <p className={styles.switchLine}>
              {signup ? 'Already have an account?' : 'Need an account?'}{' '}
              <button
                type="button"
                className={styles.textLink}
                onClick={signup ? goLogin : goSignup}
              >
                {signup ? 'Log in' : 'Create account'}
              </button>
            </p>
          </div>
        </div>
      )}

      {askLeave ? (
        <div className={styles.confirmScrim}>
          <div className={styles.confirmCard} role="alertdialog" aria-labelledby="auth-leave-ask">
            <p id="auth-leave-ask" className={styles.confirmAsk}>
              Are you sure you want to close?
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={`${soft.btn} ${styles.confirmStay}`}
                onClick={() => setAskLeave(false)}
              >
                Stay
              </button>
              <button
                type="button"
                className={`${soft.action} ${styles.confirmLeave}`}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

// The invite code to send with an account-creating POST; omitted when none.
function storedReferralCode(): string | undefined {
  return readReferredBy() ?? undefined
}

function paneFromMode(mode: AuthShellMode): Pane {
  if (mode === 'signup') return 'signup'
  if (mode === 'login') return 'login'
  if (mode === 'confirm') return 'confirm'
  if (mode === 'ton-unlock') return 'ton-unlock'
  if (mode === 'ton-recovery') return 'ton-recovery'
  if (mode === 'passkey') return 'chooser'
  return 'chooser'
}

function isWrongAccount(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'wrong_account'
}

function confirmFailMessage(err: unknown): string {
  if (isWrongAccount(err)) return WRONG_ACCOUNT
  if (err instanceof ApiError && err.code === 'register_required') return WRONG_ACCOUNT
  return WRONG_ACCOUNT
}

async function prepareFirstWalletBundle(opts: {
  password?: string
  recoveryKey: string
  loginSecret?: Uint8Array
}) {
  const keyring = await createAccountKeyringV2({
    password: opts.password,
    recoveryKey: opts.recoveryKey,
    loginSecret: opts.loginSecret,
  })
  const walletPromise = import('../../lib/tonMnemonic').then(async (mnemonic) => {
    const words = await mnemonic.generateTonMnemonic()
    const derived = await mnemonic.deriveV5R1FromMnemonic(words)
    return { words, derived }
  })
  const wallet = await walletPromise
  const words = wallet.words.join(' ')
  const { persistDeviceWalletSeed } = await import('../../lib/deviceWalletSeed')
  await persistDeviceWalletSeed(wallet.derived.address.friendly, words)
  const seed = await encryptSeedToAccountVault(keyring.publicKeyB64, words)
  return { keyring, derived: wallet.derived, seed }
}

function socialFailMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'proof_expired') return 'Login expired. Connect again.'
    return err.message
  }
  return 'Could not create the account. Try again.'
}

function tonConnectFailMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return 'TON Connect isn’t enabled on this preview. Use email.'
    if (err.code === 'invalid_wallet_proof') return 'Wallet proof is invalid. Connection refused.'
    if (err.code === 'proof_expired') return 'Wallet proof expired. Connect again.'
    return err.message
  }
  if (err instanceof Error && err.message) {
    return (
      err.message.replace(/^\[TON_CONNECT_SDK_ERROR\]\s*/i, '').trim() ||
      'Could not connect the wallet. Try again.'
    )
  }
  return 'Could not connect the wallet. Try again.'
}

// A wallet cannot be created without SubtleCrypto, and the browser withholds it
// outside a secure context. Served over plain http the key derivation throws
// before any request is made — which is why the failure never reached the API
// log and why "Try again" was a lie: retrying can never work. Measured in the
// operator's own browser on http://148.251.12.150:5173: isSecureContext false,
// crypto.subtle undefined, navigator.clipboard undefined.
const INSECURE_CONTEXT =
  'This page is served over plain http, where the browser withholds the ' +
  'cryptography a wallet needs. Open the https address to create an account.'

function walletCryptoAvailable(): boolean {
  return typeof globalThis.isSecureContext === 'boolean'
    ? globalThis.isSecureContext && !!globalThis.crypto?.subtle
    : !!globalThis.crypto?.subtle
}

// navigator.clipboard is secure-context only. The recovery key is shown once,
// so a copy button that silently does nothing is worse than no button.
async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // fall through to the selection path
  }
  try {
    const area = document.createElement('textarea')
    area.value = value
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}

function signupFailMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'email_taken' || err.status === 409) {
      return 'This email is already registered'
    }
    return err.message
  }
  return 'Could not create the account. Try again.'
}

function MethodButton({
  pending,
  disabled,
  icon,
  onClick,
  children,
}: {
  pending: boolean
  disabled: boolean
  icon: ReactNode
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      className={`${soft.btn} ${styles.method}`}
      onClick={onClick}
      disabled={disabled}
      aria-busy={pending || undefined}
    >
      {/* one 22px box for the mark and for the spinner that stands in for it:
          the words do not move when the button goes busy */}
      <span className={styles.methodMark}>{pending ? <SoftSpinner size={18} /> : icon}</span>
      {children}
    </button>
  )
}

// The one Telegram glyph of every surface: a size, nothing of its own
// (common/telegramGlyph.test pins it). Its colour is the row's to give.
function TelegramMark() {
  return <SocialGlyph type="telegram" size={18} />
}

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.6 32.4 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5Z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7 12.9 19.6C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7Z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.1 0 9.8-1.9 13.3-5.2l-6.1-5.2C29.2 35.3 26.7 36 24 36c-5.2 0-9.6-3.5-11.2-8.3l-6.5 5C9.5 39.6 16.2 44 24 44Z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.2-3.5 5.8-6.6 7.3l6.1 5.2C38.2 37.3 44 31.5 44 24c0-1.2-.1-2.3-.4-3.5Z"
      />
    </svg>
  )
}
